// ADR-012 i18n — "settings" cluster label catalog (Tenant Settings tab, WS3
// Feature 1). Spread into the central LABELS in labels.ts. EN values are the
// canonical copy; HE values are the Hebrew translation.
//
// Section keys and per-key metadata are looked up dynamically in
// `TenantSettingsEditor.tsx` against the raw `sectionKey` / `key` strings the
// backend returns from `GET /api/config/tenant-settings/{tenantId}`. An
// unrecognised section or key falls back to a humanized version of the raw
// string, so a backend-side key rename/addition never hides a field — it
// just loses its friendly copy until this catalog is updated.
export const SETTINGS_LABELS = {
  // ═══════════════════════════════════════════════════════════════════════
  //  SettingsPage — new "General" tab
  // ═══════════════════════════════════════════════════════════════════════
  settingsTabGeneral: { en: 'General', he: 'כללי' },

  // ═══════════════════════════════════════════════════════════════════════
  //  SettingsPage — "Console Settings" tab (ADR-033 D8, RepairList OP-1096 section 140)
  //  General (PR-A console settings) + Conversation Statuses (ADR-017 conversation states) + Tags, all
  //  created ONLY here; the Agent Console attaches and moves, it never creates. The section titles of the
  //  last two come from the existing Conversation States / Tags clusters.
  // ═══════════════════════════════════════════════════════════════════════
  settingsTabConsoleSettings: { en: 'Console Settings', he: 'הגדרות קונסולה' },
  consoleSettingsIntro: {
    en: 'What agents see in the Agent Console. Agents move conversations between these conversation statuses and attach these tags; they cannot create either.',
    he: 'מה שהנציגים רואים בקונסולת הנציגים. נציגים מעבירים שיחות בין סטטוסי השיחה האלה ומצמידים את התגיות האלה, אך אינם יכולים ליצור אף אחד מהם.',
  },
  consoleSettingsSectionsAria: { en: 'Console settings sections', he: 'חלקי הגדרות הקונסולה' },
  consoleSettingsGeneralTab: { en: 'General', he: 'כללי' },
  consoleSettingsFoldersTab: { en: 'Conversation Statuses', he: 'סטטוסי שיחה' },
  consoleSettingsTagsTab: { en: 'Tags', he: 'תגיות' },

  // PR-A — Console Settings > General (GET/PUT /api/sessions/console-settings). HE NEEDS NATIVE REVIEW
  // (all but consolePrivateLabel / consoleHideTagsLabel / consoleSettingsGeneralTab, which the operator supplied).
  consoleGeneralTitle: { en: 'General', he: 'כללי' },
  consolePrivateLabel: { en: 'Private conversations', he: 'שיחות פרטיות' },
  consolePrivateExplain: {
    en: 'When on, a conversation an agent owns is visible only to that agent and to admins and senior agents; other agents see only conversations nobody owns.',
    he: 'כאשר האפשרות פעילה, שיחה שבבעלות נציג גלויה רק לו, למנהלים ולנציגים בכירים; נציגים אחרים רואים רק שיחות שאין להן בעלים.',
  },
  consolePrivateInherited: {
    en: 'Follows the platform default, which is currently {value}.',
    he: 'לפי ברירת המחדל של הפלטפורמה, שכרגע {value}.',
  },
  consolePrivateExplicit: {
    en: 'Set for this tenant: {value}.',
    he: 'הוגדר במפורש עבור ארגון זה: {value}.',
  },
  consolePrivateOn: { en: 'on', he: 'פעילה' },
  consolePrivateOff: { en: 'off', he: 'כבויה' },
  consolePrivateUseDefault: { en: 'Use platform default', he: 'שימוש בברירת המחדל של הפלטפורמה' },
  consolePrivateConfirmTitle: { en: 'Change private conversations?', he: 'לשנות את השיחות הפרטיות?' },
  consolePrivateConfirmOn: {
    en: 'Turn private conversations on? Agents will stop seeing conversations that other agents own (admins and senior agents still see them).',
    he: 'להפעיל שיחות פרטיות? נציגים יפסיקו לראות שיחות שבבעלות נציגים אחרים (מנהלים ונציגים בכירים עדיין יראו אותן).',
  },
  consolePrivateConfirmOff: {
    en: 'Turn private conversations off? Every agent will be able to see every conversation, including the ones other agents own.',
    he: 'לכבות שיחות פרטיות? כל נציג יוכל לראות כל שיחה, כולל שיחות שבבעלות נציגים אחרים.',
  },
  consolePrivateConfirmDefault: {
    en: 'Follow the platform default? Whether conversations are private will then be decided by the platform default, and will change when it changes.',
    he: 'לעבור לברירת המחדל של הפלטפורמה? ההחלטה אם שיחות הן פרטיות תיקבע לפי ברירת המחדל של הפלטפורמה ותשתנה כשהיא משתנה.',
  },
  consoleHideTagsLabel: { en: 'Hide Tags folders in the Agent Console', he: 'הסתר את תיקיות התגיות בקונסולת הנציגים' },
  consoleHideTagsExplain: {
    en: 'Only the Tags section of the folder list is hidden; agents can still see and change the tags on a conversation.',
    he: 'רק אזור התגיות ברשימת התיקיות מוסתר; הנציגים עדיין יכולים לראות ולשנות את התגיות של שיחה.',
  },
  consoleEditClosedLabel: {
    en: 'Let every agent see and edit closed conversations',
    he: 'לאפשר לכל נציג לראות ולערוך שיחות סגורות',
  },
  consoleEditClosedExplain: {
    en: 'When on, any agent can open and read a closed conversation, including its messages, files and internal notes, and change its tags, status, subject and CRM ticket properties, even when another agent owns it. Open conversations stay private. Sending messages, taking over and closing still follow the owner. Off by default.',
    he: 'כשהאפשרות פעילה, כל נציג יכול לפתוח ולקרוא שיחה סגורה, כולל ההודעות, הקבצים וההערות הפנימיות שלה, ולשנות את התגיות, הסטטוס, הנושא ותכונות הכרטיס ב-CRM שלה, גם אם היא בבעלות נציג אחר. שיחות פתוחות נשארות פרטיות. שליחת הודעות, השתלטות וסגירה עדיין נשארות אצל הבעלים. כבויה כברירת מחדל.',
  },
  consoleSettingsPropagation: {
    en: 'Changes reach open Agent Consoles within about 10 seconds.',
    he: 'השינויים מגיעים לקונסולות נציגים פתוחות תוך כ-10 שניות.',
  },
  consoleSettingsLoading: { en: 'Loading console settings…', he: 'טוען הגדרות קונסולה…' },
  consoleSettingsLoadError: {
    en: 'Could not load the console settings, so nothing is shown here rather than a guess.',
    he: 'לא ניתן היה לטעון את הגדרות הקונסולה, ולכן לא מוצג כאן דבר במקום ניחוש.',
  },
  consoleSettingsSaveError: {
    en: 'The change was not saved. What you see is what is in effect.',
    he: 'השינוי לא נשמר. מה שמוצג הוא מה שבתוקף.',
  },
  consoleSettingsForbidden: {
    en: 'You do not have permission to change the console settings.',
    he: 'אין לך הרשאה לשנות את הגדרות הקונסולה.',
  },
  consoleSettingsReadOnly: {
    en: 'Read only: changing these settings needs the config.edit permission.',
    he: 'לקריאה בלבד: שינוי ההגדרות האלה דורש את ההרשאה config.edit.',
  },
  consoleSettingsSaving: { en: 'Saving…', he: 'שומר…' },

  // ═══════════════════════════════════════════════════════════════════════
  //  TenantSettingsEditor — chrome
  // ═══════════════════════════════════════════════════════════════════════
  tenantSettingsHeading: { en: 'General Settings', he: 'הגדרות כלליות' },
  tenantSettingsIntro: {
    en: 'Tune conversation, takeover, notification, media, localization, and privacy behavior for this tenant.',
    he: 'כוונן את התנהגות השיחות, ההשתלטות, ההתראות, המדיה, הלוקליזציה והפרטיות עבור ארגון זה.',
  },
  tenantSettingsLoading: { en: 'Loading settings…', he: 'טוען הגדרות…' },
  tenantSettingsEmpty: { en: 'No settings to show.', he: 'אין הגדרות להצגה.' },
  tenantSettingsReadonlyBanner: {
    en: 'You have read-only access to these settings.',
    he: 'יש לך גישת קריאה בלבד להגדרות אלו.',
  },
  tenantSettingsResetBtn: { en: 'Reset to default', he: 'אפס לברירת מחדל' },
  tenantSettingsResetting: { en: 'Resetting…', he: 'מאפס…' },
  tenantSettingsResetError: {
    en: 'Could not reset this setting. Please try again.',
    he: 'לא ניתן היה לאפס הגדרה זו. נסה שוב.',
  },
  tenantSettingsSaveError: {
    en: 'Could not save this setting. Please try again.',
    he: 'לא ניתן היה לשמור הגדרה זו. נסה שוב.',
  },
  tenantSettingsSaved: { en: 'Saved', he: 'נשמר' },
  tenantSettingsDefaultBadge: { en: 'Default', he: 'ברירת מחדל' },
  tenantSettingsCustomBadge: { en: 'Custom', he: 'מותאם אישית' },

  // ═══════════════════════════════════════════════════════════════════════
  //  Section titles + descriptions (sectionKey → label). Fallback: the raw
  //  sectionKey, capitalized, when the backend introduces an unknown section.
  // ═══════════════════════════════════════════════════════════════════════
  tenantSettingsSectionConversations: { en: 'Conversations', he: 'שיחות' },
  tenantSettingsSectionConversationsDesc: {
    en: 'How conversations are tracked and closed.',
    he: 'כיצד שיחות נעקבות ונסגרות.',
  },
  tenantSettingsSectionTakeover: { en: 'Takeover', he: 'השתלטות' },
  tenantSettingsSectionTakeoverDesc: {
    en: 'Rules for when a human agent takes a conversation over from the bot.',
    he: 'כללים למקרים שבהם נציג אנושי משתלט על שיחה מהבוט.',
  },
  tenantSettingsSectionNotifications: { en: 'Notifications', he: 'התראות' },
  tenantSettingsSectionNotificationsDesc: {
    en: 'Tenant-wide notification behavior for staff.',
    he: 'התנהגות התראות ברמת הארגון עבור הצוות.',
  },
  tenantSettingsSectionMedia: { en: 'Media', he: 'מדיה' },
  tenantSettingsSectionMediaDesc: {
    en: 'Attachment size and file-type limits.',
    he: 'מגבלות גודל וסוגי קבצים מצורפים.',
  },
  tenantSettingsSectionLocalization: { en: 'Localization', he: 'לוקליזציה' },
  tenantSettingsSectionLocalizationDesc: {
    en: 'Default language and regional behavior.',
    he: 'שפת ברירת מחדל והתנהגות אזורית.',
  },
  tenantSettingsSectionPrivacy: { en: 'Privacy', he: 'פרטיות' },
  tenantSettingsSectionPrivacyDesc: {
    en: 'Data retention and privacy controls.',
    he: 'שמירת נתונים ובקרות פרטיות.',
  },
  tenantSettingsSectionAiagent: { en: 'AI Agent', he: 'סוכן ה-AI' },
  tenantSettingsSectionAiagentDesc: {
    en: 'How the bot behaves in its replies to customers.',
    he: 'כיצד הבוט מתנהג בתשובותיו ללקוחות.',
  },

  // ═══════════════════════════════════════════════════════════════════════
  //  Per-key titles + descriptions. Fallback: a humanized version of the
  //  raw key's last dot-segment when the backend introduces an unknown key.
  //  Reconciled 2026-07-21 against the authoritative registry in
  //  ConfigurationManagementService/TenantSettingsRegistry.cs — the 10 keys
  //  below are the real ones `GET /api/config/tenant-settings/{tenantId}`
  //  returns; earlier placeholder keys/copy have been removed. Two more keys
  //  (the AiAgent.Links.* pair, WS-D.1) were added 2026-07-31, bringing the
  //  reconciled total to 12.
  // ═══════════════════════════════════════════════════════════════════════
  tenantSettingsKeyCloseAfterHoursTitle: {
    en: 'Auto-close inactive conversations after (hours)',
    he: 'סגור אוטומטית שיחות לא פעילות אחרי (שעות)',
  },
  tenantSettingsKeyCloseAfterHoursDesc: {
    en: 'Conversations with no activity for this many hours are automatically closed.',
    he: 'שיחות ללא פעילות למשך מספר שעות זה ייסגרו אוטומטית.',
  },
  tenantSettingsKeyNotifyCustomerOnTakeoverTitle: {
    en: 'Notify the customer when a human agent joins the chat',
    he: 'הודע ללקוח כאשר נציג אנושי מצטרף לשיחה',
  },
  tenantSettingsKeyNotifyCustomerOnTakeoverDesc: {
    en: 'When a human agent takes over from the bot, show the customer a system message announcing it. Off by default.',
    he: 'כאשר נציג אנושי משתלט על השיחה מהבוט, הצג ללקוח הודעת מערכת על כך. כברירת מחדל כבוי.',
  },
  tenantSettingsKeyCustomerEmailsPerDayTitle: {
    en: 'Maximum customer emails per day',
    he: 'מספר מרבי של דוא״ל ללקוח ביום',
  },
  tenantSettingsKeyCustomerEmailsPerDayDesc: {
    en: 'The most email notifications a single customer can receive in one day.',
    he: 'מספר התראות הדוא״ל המרבי שלקוח בודד יכול לקבל ביום אחד.',
  },
  tenantSettingsKeyCustomerSmsPerDayTitle: {
    en: 'Maximum customer SMS messages per day',
    he: 'מספר מרבי של הודעות SMS ללקוח ביום',
  },
  tenantSettingsKeyCustomerSmsPerDayDesc: {
    en: 'The most SMS notifications a single customer can receive in one day.',
    he: 'מספר הודעות ה-SMS המרבי שלקוח בודד יכול לקבל ביום אחד.',
  },
  tenantSettingsKeyMaxFileSizeBytesTitle: {
    en: 'Maximum attachment size (bytes)',
    he: 'גודל קובץ מצורף מרבי (בייטים)',
  },
  tenantSettingsKeyMaxFileSizeBytesDesc: {
    en: 'The largest file size, in bytes, a customer or agent can attach to a message.',
    he: 'הגודל המרבי, בבייטים, של קובץ שניתן לצרף להודעה על ידי לקוח או נציג.',
  },
  tenantSettingsKeyAllowedMimeTypesTitle: {
    en: 'Allowed attachment types',
    he: 'סוגי קבצים מצורפים מותרים',
  },
  tenantSettingsKeyAllowedMimeTypesDesc: {
    en: 'Comma-separated list of allowed MIME types for attachments (e.g. image/png,application/pdf).',
    he: 'רשימה מופרדת בפסיקים של סוגי MIME מותרים לקבצים מצורפים (למשל image/png,application/pdf).',
  },
  tenantSettingsKeySupportedLanguagesTitle: {
    en: 'Supported languages for templates',
    he: 'שפות נתמכות עבור תבניות',
  },
  tenantSettingsKeySupportedLanguagesDesc: {
    en: 'Comma-separated list of language codes this tenant’s message templates are available in.',
    he: 'רשימה מופרדת בפסיקים של קודי שפה שבהם זמינות תבניות ההודעות של ארגון זה.',
  },
  tenantSettingsKeyEnableAutoTranslationTitle: {
    en: 'Automatically translate templates',
    he: 'תרגם תבניות באופן אוטומטי',
  },
  tenantSettingsKeyEnableAutoTranslationDesc: {
    en: 'Automatically translate message templates into a customer’s language when no manual translation exists.',
    he: 'תרגם באופן אוטומטי תבניות הודעה לשפת הלקוח כאשר אין תרגום ידני זמין.',
  },
  tenantSettingsKeyPiiMaskingEnabledTitle: {
    en: 'Mask personal information in stored transcripts',
    he: 'הסתר מידע אישי בתמלולים שמורים',
  },
  tenantSettingsKeyPiiMaskingEnabledDesc: {
    en: 'When enabled, personal data (emails, phone numbers, etc.) is masked in stored conversation transcripts.',
    he: 'כאשר מופעל, מידע אישי (דוא״ל, מספרי טלפון וכו׳) יוסתר בתמלולי השיחה השמורים.',
  },
  tenantSettingsKeyLinksEnabledTitle: {
    en: 'Let the bot share links',
    he: 'אפשר לבוט לשתף קישורים',
  },
  tenantSettingsKeyLinksEnabledDesc: {
    en: 'Allow the bot to include links to your product or its documentation in replies. Off by default.',
    he: 'אפשר לבוט לצרף בתשובותיו קישורים למוצר שלך או לתיעוד שלו. כברירת מחדל כבוי.',
  },
  tenantSettingsKeyExtraAllowedDomainsTitle: {
    en: 'Additional allowed link domains',
    he: 'דומיינים נוספים המורשים לקישור',
  },
  tenantSettingsKeyExtraAllowedDomainsDesc: {
    en: 'Comma-separated extra domains the bot may link to, on top of the domains of your knowledge sources (e.g. docs.example.com,status.example.com).',
    he: 'רשימה מופרדת בפסיקים של דומיינים נוספים שהבוט יכול לקשר אליהם, בנוסף לדומיינים של מקורות הידע שלך (למשל docs.example.com,status.example.com).',
  },

  // ═══════════════════════════════════════════════════════════════════════
  //  Settings › Channel Configurations (ADR-020 Phase 2)
  //
  //  The tab formerly called "Chat Widget". Chat Widget is now a SECTION
  //  inside it, alongside WhatsApp connections — both answer the same
  //  operator question ("how is this channel wired to the outside world?"),
  //  and a tab per channel type would not scale past two.
  // ═══════════════════════════════════════════════════════════════════════
  settingsTabChannelConfigurations: { en: 'Channel Configurations', he: 'הגדרות ערוצים' },
  channelConfigSectionChatWidget: { en: 'Chat Widget', he: 'ווידג׳ט הצ׳אט' },
  channelConfigSectionWhatsApp: { en: 'WhatsApp', he: 'ווטסאפ' },

  // ── WhatsApp connections ────────────────────────────────────────────────
  waConnTitle: { en: 'WhatsApp connections', he: 'חיבורי ווטסאפ' },
  waConnDescription: {
    en: 'A connection is one 360Dialog account. It owns a single inbound webhook that covers every phone number on that account — add the connection here, paste its webhook into 360Dialog, then attach your numbers to it when you create each WhatsApp channel.',
    he: 'חיבור הוא חשבון 360Dialog אחד. הוא מחזיק כתובת webhook נכנסת אחת שמשרתת את כל מספרי הטלפון באותו חשבון — הוסיפו כאן את החיבור, הדביקו את ה-webhook ב-360Dialog, ולאחר מכן שייכו אליו את המספרים שלכם בעת יצירת כל ערוץ ווטסאפ.',
  },
  waConnLoading: { en: 'Loading WhatsApp connections...', he: 'טוען חיבורי ווטסאפ...' },
  waConnEmpty: {
    en: 'No WhatsApp connections yet.',
    he: 'אין עדיין חיבורי ווטסאפ.',
  },
  waConnAddButton: { en: 'Add New Connection', he: 'הוסף חיבור חדש' },
  waConnProviderLabel: { en: 'Provider', he: 'ספק' },
  waConnProviderDialog360: { en: '360Dialog', he: '360Dialog' },
  waConnProviderOnlyHint: {
    en: '360Dialog is the only supported provider today.',
    he: '360Dialog הוא הספק היחיד הנתמך כרגע.',
  },
  waConnNameLabel: { en: 'Connection name', he: 'שם החיבור' },
  waConnNamePlaceholder: { en: 'e.g. Main WABA', he: 'למשל: חשבון ראשי' },
  waConnNameHint: {
    en: 'Shown when you pick a connection on the WhatsApp channel form, so make it tell one 360Dialog account from another.',
    he: 'מוצג בעת בחירת חיבור בטופס ערוץ הווטסאפ, לכן כדאי שיבחין בין חשבונות 360Dialog שונים.',
  },
  waConnNameRequired: { en: 'A connection name is required.', he: 'נדרש שם חיבור.' },
  waConnCreateSubmit: { en: 'Create connection', he: 'צור חיבור' },
  waConnCreating: { en: 'Creating...', he: 'יוצר...' },
  waConnCancel: { en: 'Cancel', he: 'ביטול' },

  waConnWebhookLabel: { en: 'Webhook URL', he: 'כתובת Webhook' },
  waConnWebhookCopy: { en: 'Copy', he: 'העתק' },
  waConnWebhookCopied: { en: 'Copied', he: 'הועתק' },
  waConnWebhookUnavailable: {
    en: 'The platform has no public webhook base URL configured yet, so the full address cannot be shown. Ask your administrator to set ChannelAdapter.WhatsApp.PublicWebhookBaseUrl — note 360Dialog rejects domains containing underscores or an explicit port.',
    he: 'לא הוגדרה כתובת בסיס ציבורית ל-webhook במערכת, ולכן לא ניתן להציג את הכתובת המלאה. בקשו מהמנהל להגדיר את ChannelAdapter.WhatsApp.PublicWebhookBaseUrl — שימו לב ש-360Dialog אינו מקבל דומיינים עם קו תחתון או עם ציון פורט מפורש.',
  },
  waConnSecretHeaderLabel: { en: 'Secret header', he: 'כותרת סוד' },
  waConnAttachedLabel: { en: 'Numbers attached', he: 'מספרים משויכים' },
  waConnLastVerifiedLabel: { en: 'Last verified delivery', he: 'משלוח מאומת אחרון' },
  waConnNeverVerified: {
    en: 'never — no authenticated delivery has arrived yet',
    he: 'מעולם — עדיין לא התקבל משלוח מאומת',
  },
  waConnNoSecret: { en: 'No secret has been minted for this connection.', he: 'לא הונפק סוד עבור חיבור זה.' },

  waConnSecretRevealTitle: {
    en: 'Webhook secret — copy it into 360Dialog now, it will not be shown again',
    he: 'סוד ה-Webhook — העתיקו אותו ל-360Dialog עכשיו, הוא לא יוצג שוב',
  },
  waConnSetupInstructions: {
    en: 'Press "Connect at 360Dialog" below and we register this for every attached number. To do it by hand instead: in the 360Dialog Hub, set the WABA (or per-number) webhook to the URL above and add the secret as a custom header with the name shown. Without the header every delivery is rejected.',
    he: 'לחצו על "חבר ב-360Dialog" למטה ואנחנו נרשום זאת עבור כל מספר משויך. לביצוע ידני: ב-360Dialog Hub, הגדירו את ה-webhook של ה-WABA (או של המספר) לכתובת שלמעלה והוסיפו את הסוד ככותרת מותאמת בשם המוצג. ללא הכותרת, כל משלוח יידחה.',
  },
  // Shown instead of the paste-it-yourself instructions when the secret came from a provision run:
  // it is already registered, and telling the operator to paste it would send them to the Hub to
  // redo work we just did — and risk them pasting it against the wrong scope.
  waConnProvisionSecretNote: {
    en: 'We already registered this secret at 360Dialog for the numbers listed below — there is nothing to paste. Keep a copy only if you use this WABA somewhere else too.',
    he: 'כבר רשמנו את הסוד הזה ב-360Dialog עבור המספרים המפורטים למטה — אין מה להדביק. שמרו עותק רק אם אתם משתמשים ב-WABA הזה גם במקום אחר.',
  },

  // OP-628 — the Provision button. Registers the shared webhook at 360Dialog for every attached
  // number, using each number's own API key (a connection has none of its own), replacing what used
  // to be a hand-typed Hub step.
  waConnProvision: { en: 'Connect at 360Dialog', he: 'חבר ב-360Dialog' },
  waConnProvisioning: { en: 'Connecting...', he: 'מחבר...' },
  waConnProvisionHint: {
    en: 'Registers this webhook at 360Dialog for every number attached to this connection, so you do not have to set it in the Hub by hand. It replaces the secret each time, so run it once and again after attaching a number.',
    he: 'רושם את ה-webhook הזה ב-360Dialog עבור כל מספר המשויך לחיבור, כך שאין צורך להגדיר זאת ידנית ב-Hub. הפעולה מחליפה את הסוד בכל הרצה, לכן הריצו אותה פעם אחת ושוב לאחר שיוך מספר.',
  },
  waConnProvisionConfirmTitle: { en: 'Connect this WABA at 360Dialog?', he: 'לחבר את ה-WABA הזה ב-360Dialog?' },
  waConnProvisionConfirmBody: {
    en: 'Every number attached to this connection will be pointed at this webhook, and the secret will be replaced. Any number that fails will stop receiving until you provision it again — the result is listed per number.',
    he: 'כל מספר המשויך לחיבור זה יופנה ל-webhook הזה, והסוד יוחלף. מספר שייכשל יפסיק לקבל הודעות עד להרצה חוזרת — התוצאה מוצגת לכל מספר בנפרד.',
  },
  waConnProvisionConfirmCta: { en: 'Connect', he: 'חבר' },
  waConnProvisionResultsTitle: { en: 'Result per number', he: 'תוצאה לכל מספר' },
  waConnProvisionOk: { en: 'Receiving', he: 'מקבל' },
  waConnProvisionFailed: { en: 'Not receiving', he: 'אינו מקבל' },
  waConnProvisionAllOk: {
    en: 'Every number on this connection is now registered at 360Dialog.',
    he: 'כל המספרים בחיבור זה רשומים כעת ב-360Dialog.',
  },
  waConnProvisionError: { en: 'Could not connect this WABA.', he: 'לא ניתן היה לחבר את ה-WABA.' },

  // OP-629 — the inverse of Provision, and the only clean way to retire a WABA: a registration can
  // only be cleared with the API key of the number that owns it, and both delete paths take those
  // keys away before cleanup becomes possible.
  waConnDisconnect: { en: 'Disconnect at 360Dialog', he: 'נתק מ-360Dialog' },
  waConnDisconnecting: { en: 'Disconnecting...', he: 'מנתק...' },
  waConnDisconnectConfirmTitle: { en: 'Stop this WABA receiving?', he: 'להפסיק קבלת הודעות ב-WABA זה?' },
  waConnDisconnectConfirmBody: {
    en: 'We will clear this webhook from every number attached to this connection, so they stop receiving WhatsApp messages. Nothing is deleted — the connection, its numbers and its secret all stay, and Connect at 360Dialog puts it back. Do this BEFORE deleting the numbers: once they are gone, their API keys go with them and the registrations can no longer be cleared.',
    he: 'ננקה את ה-webhook מכל מספר המשויך לחיבור, כך שיפסיקו לקבל הודעות ווטסאפ. שום דבר לא נמחק — החיבור, המספרים והסוד נשארים, ו"חבר ב-360Dialog" יחזיר את המצב. בצעו זאת לפני מחיקת המספרים: לאחר מחיקתם, מפתחות ה-API נמחקים איתם ולא ניתן יהיה לנקות את הרישומים.',
  },
  waConnDisconnectConfirmCta: { en: 'Disconnect', he: 'נתק' },
  waConnDisconnectAllOk: {
    en: 'Cleared. No number on this connection is receiving through it any more.',
    he: 'נוקה. אף מספר בחיבור זה אינו מקבל דרכו יותר.',
  },
  waConnDisconnectCleared: { en: 'Cleared', he: 'נוקה' },
  waConnDisconnectNotCleared: { en: 'May still deliver', he: 'ייתכן שעדיין מקבל' },
  waConnDisconnectError: { en: 'Could not disconnect this WABA.', he: 'לא ניתן היה לנתק את ה-WABA.' },

  waConnRotate: { en: 'Replace secret', he: 'החלף סוד' },
  waConnRotating: { en: 'Replacing...', he: 'מחליף...' },
  waConnRotateConfirmTitle: { en: 'Replace this webhook secret?', he: 'להחליף את סוד ה-Webhook הזה?' },
  waConnRotateConfirmBody: {
    en: 'Incoming WhatsApp messages on every number attached to this connection will be rejected from the moment you confirm, until you paste the new secret into 360Dialog. We cannot set it for you.',
    he: 'הודעות ווטסאפ נכנסות בכל המספרים המשויכים לחיבור זה יידחו מרגע האישור, עד שתדביקו את הסוד החדש ב-360Dialog. איננו יכולים להגדיר אותו עבורכם.',
  },
  waConnRotateConfirmCta: { en: 'Replace secret', he: 'החלף סוד' },

  waConnDelete: { en: 'Remove connection', he: 'הסר חיבור' },
  waConnDeleting: { en: 'Removing...', he: 'מסיר...' },
  waConnDeleteConfirmTitle: { en: 'Remove this connection?', he: 'להסיר את החיבור הזה?' },
  waConnDeleteConfirmBody: {
    en: 'The webhook address stops working. Remove it in the 360Dialog Hub too, or 360Dialog will keep sending to an address that no longer exists.',
    he: 'כתובת ה-Webhook תפסיק לפעול. הסירו אותה גם ב-360Dialog Hub, אחרת 360Dialog ימשיך לשלוח לכתובת שאינה קיימת עוד.',
  },
  waConnDeleteConfirmCta: { en: 'Remove connection', he: 'הסר חיבור' },
  waConnDeleteBlocked: {
    en: 'Numbers still route through this connection. Move or delete them first.',
    he: 'מספרים עדיין מנותבים דרך חיבור זה. העבירו או מחקו אותם תחילה.',
  },
} satisfies Record<string, { en: string; he: string }>;
